import {memo, useEffect} from 'react';

import styled, {keyframes} from 'styled-components';

import ClearSearch from '~/assets/images/svgIconsAsText/clear_search.svg?react';
import Button from '~/shared/components/Button';
import {getLocalizationService} from '~/shared/services/localisationService';
import {flexColumn, flexColumnCenter} from '~/shared/theme/FlexLayout';
import {body12Bold, body14Bold, body20Bold} from '~/shared/theme/typography';
import TenbisCredit from '~/assets/images/icons/tenbisCredit.svg?react';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import {User} from '~/shared/store/models';
import {Skeleton, SkeletonLoader} from '~/shared/components/Loaders';
import CreditLoadingFailed from '~/shared/components/CreditLoadingFailed';
import {UserDeviceState} from '~/shared/store/storeModules/userDevice/userDeviceReducer';
import {ApiError} from '~/shared/services/apiErrorService';

import {PriceLabel} from '../../PriceLabels';

import headerBgUrl from './assets/header.jpg';
import {ActionMenuLink} from './types';
import ActionMenuLinks from './ActionMenuLinks';

const widthTransition = keyframes`
  from {
    width: 0;
  }

  to {
    width: 100%;
  }
`;

const Root = styled.div`
  position: fixed;
  overflow-y: auto;
  top: 0;
  ${flipOnLTR`
    left: 0;
  `}
  z-index: ${({theme}) => theme.zIndex.sideMenu};
  width: 100vw;
  height: 100vh;
  background-color: ${({theme}) => theme.colors.lightBackground2};
  animation: ${widthTransition} 0.3s ease-out 1;
`;

const HeaderSection = styled.div`
  ${flexColumnCenter}
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 245px;
`;

const PersonalInfo = styled.div`
  ${flexColumn}
  color: ${({theme}) => theme.colors.primaryText};
  height: 189px;
  min-height: 189px;
  width: 100%;
  background-image: url(${headerBgUrl});
  background-size: cover;
  overflow: hidden;
  padding: 0 16px;

  div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CreditBalance = styled.div`
  display: flex;
  height: 80px;
  border-radius: 16px;
  width: 100%;
  box-shadow: ${({theme}) => theme.shadows.elevationCard};
  background-color: ${({theme}) => theme.colors.surface};
`;

const CreditBalanceWrapper = styled.div`
  position: relative;
  top: -40px;
  width: 100%;
  height: 80px;
  padding: 0 16px;
`;

const ActionLinksWrapper = styled.div`
  margin: 0 16px 16px 16px;
  box-shadow: ${({theme}) => theme.shadows.elevationCard};
  border-radius: 16px;
  overflow: hidden;
`;

const CloseButtonWrapper = styled.div`
  display: flex;
  height: 48px;
  width: 100%;
  justify-content: flex-end;
  align-items: flex-end;
`;

const CloseSvg = styled(ClearSearch)`
  width: 34px;
  height: 34px;
  color: ${({theme}) => theme.colors.secondaryText};
`;

const CloseButton = styled(Button)`
  ${flexColumnCenter}
  border-radius: 50%;
  width: 32px;
  height: 32px;
  background-color: ${({theme}) => theme.colors.surface};
`;

const UserName = styled.div`
  margin-top: 8px;
  ${body20Bold}
  line-height: 28px;
`;

const UserPhoneNumber = styled.div`
  ${body14Bold}
  line-height: 20px;
`;

const UserEmail = styled.div`
  ${body14Bold}
  line-height: 20px;
  margin-bottom: 4px;
`;

const StyledTenbisCreditImage = styled(TenbisCredit)`
  width: 26px;
  height: 26px;
  margin: auto 0;
  ${flipOnLTR`
    margin-right: 16px;
    margin-left: 10px;
  `}

  path {
    fill: ${({theme}) => theme.colors.contentBrand};
  }
`;

const BalanceInfo = styled.div`
  ${flexColumn}
  color: ${({theme}) => theme.colors.charcoal80};
  justify-content: center;
`;

const BalanceAmount = styled(PriceLabel)`
  ${body20Bold}
  line-height: 28px;
`;

const BalanceLabel = styled.div`
  ${body12Bold}
  margin-top: 4px;
  line-height: 16px;
`;

const StyledSkeleton = styled(Skeleton)`
  border-radius: 6px;
`;

interface ActionMenuContentProps {
  actionMenuLinks: ActionMenuLink[];
  toggleMenuOpen: (clickEvent?: Event) => void;
  burgerMenu?: boolean;
  topOffset?: number;
  userData?: User;
  userDevice: UserDeviceState;
  tenbisCreditBalance: number;
  fetchMoneyCards: () => void;
  isMoneyCardsLoaded?: boolean;
  isMoneyCardsLoading?: boolean;
  moneyCardsError?: ApiError;
}

const BalanceSkeleton = () => {
  return <StyledSkeleton width={96} height={28} />;
};

const PrivateUserActionMenuContent = ({actionMenuLinks, toggleMenuOpen, userData, tenbisCreditBalance, isMoneyCardsLoaded, isMoneyCardsLoading, fetchMoneyCards, moneyCardsError}: ActionMenuContentProps) => {
  const {t} = getLocalizationService();

  useEffect(() => {
    if (!isMoneyCardsLoaded && !isMoneyCardsLoading) {
      fetchMoneyCards();
    }
  }, [fetchMoneyCards, isMoneyCardsLoaded, isMoneyCardsLoading]);

  const handleOnClick = (link: ActionMenuLink) => {
    link.onClick?.();
  };

  return (
    <Root>
      <HeaderSection>
        <PersonalInfo>
          <CloseButtonWrapper>
            <CloseButton onClick={toggleMenuOpen}>
              <CloseSvg viewBox="0 0 24 24" alt={t('close')} />
            </CloseButton>
          </CloseButtonWrapper>
          <UserName data-test-id="userName">{`${userData?.firstName} ${userData?.lastName}`}</UserName>
          <UserEmail data-test-id="userEmail">{userData?.email}</UserEmail>
          <UserPhoneNumber data-test-id="userPhone">{userData?.cellphone}</UserPhoneNumber>
        </PersonalInfo>
        <CreditBalanceWrapper>
          <CreditBalance>
            <StyledTenbisCreditImage viewBox="0 0 24 24"/>
            {moneyCardsError ? (
              <CreditLoadingFailed onRetry={fetchMoneyCards} />
            ) : (
              <BalanceInfo>
                <SkeletonLoader shouldShowLoader={!isMoneyCardsLoaded} LoaderComponent={BalanceSkeleton}>
                  <BalanceAmount price={tenbisCreditBalance} priceDataTestId="userCreditBalance"/>
                </SkeletonLoader>
                <BalanceLabel>
                  {t('credit_balance')}
                </BalanceLabel>
              </BalanceInfo>
            )}
          </CreditBalance>
        </CreditBalanceWrapper>
      </HeaderSection>
      <ActionLinksWrapper>
        <ActionMenuLinks
          links={actionMenuLinks}
          onClick={handleOnClick}
          userData={userData}
          closeMenu={toggleMenuOpen}
        />
      </ActionLinksWrapper>
    </Root>
  );
};

export default memo(PrivateUserActionMenuContent);
